import { AlreadyPlottedPlayer, ChemistryPlayerPositions, TeamPasses } from "../types/IOption";
import SwapPlayer from "../assets/icons/SwapPlayer.png";
import VisibleON from "../assets/icons/VisibleON.png";
import { Theme } from "../styles/Theme";
import * as d3 from "d3";
import { CurrentView, HullData } from "../types/IPitchProps";
import noImagePlayer from "../assets/icons/noImagePlayer.svg";
import { IGamemodelData } from "../components/ClubDetail/Performance/TeamPlayingStyle";
import { useState } from "react";

export const appendRect = (
  svg: d3.Selection<SVGGElement, any, null, undefined> | d3.Selection<SVGSVGElement, unknown, any, undefined>,
  width: number,
  height: number,
  X: number,
  Y: number,
  fill: string,
  roundedness: number,
  strokeWidth: number,
  strokeColor: string,
  opacity: number,
  className?: string,
  transformString?: string,
  onMouseOver?: () => void,
  onMouseOut?: () => void
) => {
  svg
    // @ts-ignore
    .append("rect")
    .attr("class", className ?? "none")
    .attr("width", width)
    .attr("height", height)
    .attr("x", X)
    .attr("y", Y)
    .style("fill", fill)
    .attr("rx", roundedness)
    .style("stroke-width", strokeWidth)
    .style("stroke", strokeColor)
    .style("opacity", opacity)
    .attr("transform", function (d: any) {
      return transformString ?? "none";
    })
    .on("mouseover", function () {
      if (onMouseOver) {
        onMouseOver();
      }
    })
    .on("mouseout", function () {
      if (onMouseOut) {
        onMouseOut();
      }
    });
};
export const appendCircle = (
  svg: d3.Selection<SVGGElement, unknown, null, undefined> | d3.Selection<SVGSVGElement, unknown, any, undefined>,
  centerX: number,
  centerY: number,
  size: number,
  fill: string,
  strokeDasharray: string,
  strokeWidth: number,
  strokeColor: string,
  className?: string
) => {
  svg
    // @ts-ignore
    .append("circle")
    .attr("class", className ?? "none")
    .attr("r", size)
    .style("fill", fill)
    .attr("stroke-width", strokeWidth)
    .attr("stroke", strokeColor)
    .attr("stroke-dasharray", strokeDasharray)
    .attr("cx", centerX)
    .attr("cy", centerY);
};
export const drawMiddleRect = (
  svg: d3.Selection<SVGGElement, any, null, undefined> | d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  theme: Theme,
  icon: string,
  textArray: string[]
) => {
  const centerX = xScale(105 / 2);
  const centerY = yScale(68 / 2);
  // @ts-ignore
  const container = svg.append("g").attr("class", "info-container");
  container
    .append("rect")
    .attr("x", centerX - 80)
    .attr("y", centerY - 80)
    .attr("rx", 15)
    .attr("width", 160)
    .attr("height", 160)
    .attr("stroke-width", 1)
    .attr("fill", theme.middlePitchInfoRect)
    .attr("stroke", theme.borderColor);
  container
    .append("text")
    .attr("text-anchor", "middle")
    .attr("x", centerX)
    .attr("y", centerY + 16)
    .attr("fill", theme.playerColor)
    .selectAll("tspan")
    .data(textArray)
    .enter()
    .append("tspan")
    .attr("x", centerX)
    .attr("dy", (d: any, i: any) => (i === 0 ? "0em" : "1.5em"))
    .text((d: any) => d)
    .style("font", "16px Aeonik")
    .style("font-weight", "700");
  container
    .append("svg:image")
    .attr("class", "pitch-middle-image")
    .attr("xlink:href", icon)
    .attr("x", centerX - 25)
    .attr("y", centerY - 68) // 58 / 2 + 20
    .attr("width", 48)
    .attr("height", 48);
  // .style("filter", theme.PitchColor === "#1B1B1B" ? "none" : "brightness(100%) invert(100%)");
};
export const appendText = (
  parent: d3.Selection<SVGGElement, any, null, undefined> | d3.Selection<SVGSVGElement, unknown, any, undefined>,
  X: number,
  Y: number,
  text: string | number,
  fillColor: string,
  fontFamily: string,
  fontWeight: number,
  fontSize: number,
  opacity: number,
  textAnchor: "end" | "middle" | "start",
  textClass?: string,
  cursor?: "pointer"
) => {
  parent
    // @ts-ignore
    .append("text")
    .attr("class", textClass ?? "")
    .attr("opacity", opacity)
    .attr("text-anchor", textAnchor)
    .attr("dominant-baseline", "bottom")
    .attr("x", X)
    .attr("y", Y)
    .text(text)
    .style("fill", fillColor)
    .style("font-family", fontFamily)
    .style("font-weight", fontWeight)
    .style("font-size", `${fontSize}px`)
    .attr("cursor", cursor)
    .attr("pointer-events", "none");
};
export const getColorByValue = (value: number) => {
  if (value > 0 && value <= 19.99) {
    return "var(--UI-red-500, #FF3864)";
  } else if (value >= 20 && value <= 39.99) {
    return "var(--Brand-orange-500, #FF7800)";
  } else if (value >= 40 && value <= 59.99) {
    return "var(--UI-yellow-500, #F2E94E)";
  } else if (value >= 60 && value <= 79.99) {
    return "#C9F0B6";
  } else if (value >= 80 && value <= 98.99) {
    return "var(--Brand-green-2-500, #4AF075)";
  } else if (value >= 99) {
    return "#00D1A8";
  } else if (value === 0) {
    return "#DADADA";
  } else {
    return "red";
  }
};
export const getArchetypeColorByValue = (value: number) => {
  if (value >= 1 && value <= 10) {
    return "#C9F0B6";
  } else if (value >= 11 && value <= 20) {
    return "var(--Brand-green-2-500, #4AF075)";
  } else if (value >= 21) {
    return "#00D1A8";
  }
};
const showHoverIcons = (svg: d3.Selection<SVGSVGElement, unknown, any, undefined>, player: ChemistryPlayerPositions) => {
  svg.selectAll(`image.hoverIcon${player.playerId}`).style("opacity", 1);
  svg.selectAll(`rect.hoverRect${player.playerId}`).style("opacity", 1);
};
const hideHoverIcons = (svg: d3.Selection<SVGSVGElement, unknown, any, undefined>, player: ChemistryPlayerPositions) => {
  svg.selectAll(`image.hoverIcon${player.playerId}`).style("opacity", 0);
  svg.selectAll(`rect.hoverRect${player.playerId}`).style("opacity", 0);
};
const showHoverText = (svg: d3.Selection<SVGSVGElement, unknown, any, undefined>, player: ChemistryPlayerPositions) => {
  svg.selectAll(`rect.hoverRect${player.playerId}`).attr("width", 110);
  svg.selectAll(`text.hoverText${player.playerId}`).style("opacity", 1);
};
const hideHoverText = (svg: d3.Selection<SVGSVGElement, unknown, any, undefined>, player: ChemistryPlayerPositions) => {
  svg.selectAll(`rect.hoverRect${player.playerId}`).attr("width", 32);
  svg.selectAll(`text.hoverText${player.playerId}`).style("opacity", 0);
};
export const calculateStrokeWidthScale = (passNetworkPlayerPasses: TeamPasses[]) => {
  const validPasses = passNetworkPlayerPasses
    .map((connection) => connection.totalPasses)
    .filter((pass) => typeof pass === "number" && !isNaN(pass)) as number[];
  return d3
    .scaleLinear()
    .domain([0, d3.max(validPasses) || 0])
    .range([1, window.innerWidth < 800 ? 5 : 15]);
};
export const drawPlayerNetworks = (
  svg: d3.Selection<SVGGElement, any, null, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  passNetworkPlayers: ChemistryPlayerPositions[],
  passNetworkPlayerPasses: TeamPasses[]
) => {
  const strokeWidthScale = calculateStrokeWidthScale(passNetworkPlayerPasses);
  const linesGroup = svg.append("g").attr("class", "lines-group");
  passNetworkPlayerPasses.forEach((connection) => {
    const startPlayer = passNetworkPlayers.find((player) => player.playerId === connection.player1);
    const endPlayer = passNetworkPlayers.find((player) => player.playerId === connection.player2);
    if (startPlayer && endPlayer) {
      const strokeWidth = strokeWidthScale(connection.totalPasses);
      const showRect = () => {
        linesGroup.selectAll(`line`).style("opacity", 0.25);
        linesGroup.selectAll(`rect`).style("opacity", 0);
        linesGroup.selectAll(`text`).style("opacity", 0);
        svg.selectAll(`line.line${startPlayer.playerId}${endPlayer.playerId}`).style("opacity", 1);
        svg.selectAll(`rect.rect${startPlayer.playerId}${endPlayer.playerId}`).style("opacity", 1);
        svg.selectAll(`text.text${startPlayer.playerId}${endPlayer.playerId}`).style("opacity", 1);
      };
      const hideRect = () => {
        linesGroup.selectAll(`line`).style("opacity", 1);
        svg.selectAll(`rect.rect${startPlayer.playerId}${endPlayer.playerId}`).style("opacity", 0);
        svg.selectAll(`text.text${startPlayer.playerId}${endPlayer.playerId}`).style("opacity", 0);
      };
      linesGroup
        .append("line")
        .attr("class", `line${startPlayer.playerId}${endPlayer.playerId}`)
        .attr("x1", xScale(startPlayer.xCoordinate))
        .attr("y1", yScale(startPlayer.yCoordinate))
        .attr("x2", xScale(endPlayer.xCoordinate))
        .attr("y2", yScale(endPlayer.yCoordinate))
        .style("stroke", getColorByValue(connection.accuracyPercent))
        .style("stroke-width", strokeWidth)
        .style("cursor", "pointer")
        .on("click", () => {
          if (svg.select(`rect.rect${startPlayer.playerId}${endPlayer.playerId}`).style("opacity") === "1") {
            hideRect();
          } else {
            showRect();
          }
        });
      let startX = xScale(startPlayer.xCoordinate);
      let startY = yScale(startPlayer.yCoordinate);
      let endX = xScale(endPlayer.xCoordinate);
      let endY = yScale(endPlayer.yCoordinate);
      let distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
      const centerX = (startX + endX) / 2;
      const centerY = (startY + endY) / 2;
      const startOffsetX = (startX + centerX) / 2;
      const startOffsetY = (startY + centerY) / 2;
      const endOffsetX = (endX + centerX) / 2;
      const endOffsetY = (endY + centerY) / 2;
      appendRect(
        linesGroup,
        35,
        28,
        startOffsetX - 35 / 2,
        startOffsetY - 28 / 2,
        getColorByValue(connection.accuracyPercent),
        4,
        1,
        "rgb(1,1,1,0.20)",
        0,
        `rect${startPlayer.playerId}${endPlayer.playerId}`
      );
      appendRect(
        linesGroup,
        35,
        28,
        endOffsetX - 35 / 2,
        endOffsetY - 28 / 2,
        getColorByValue(connection.accuracyPercent),
        4,
        1,
        "rgb(1,1,1,0.20)",
        0,
        `rect${startPlayer.playerId}${endPlayer.playerId}`
      );
      appendText(
        linesGroup,
        startOffsetX + 13,
        startOffsetY + 5,
        `${connection.player1accuracy}%`,
        connection.accuracyPercent < 49 ? "#FFFFFF" : "#111111",
        "DM Mono",
        500,
        14,
        0,
        "end",
        `text${startPlayer.playerId}${endPlayer.playerId}`
      );
      appendText(
        linesGroup,
        endOffsetX + 13,
        endOffsetY + 5,
        `${connection.player2accuracy}%`,
        connection.accuracyPercent < 49 ? "#FFFFFF" : "#111111",
        "DM Mono",
        500,
        14,
        0,
        "end",
        `text${startPlayer.playerId}${endPlayer.playerId}`
      );
    } else {
      console.warn("Start player or end player not found for connection:", connection);
    }
  });
};

const raisePlayerNames = (playerId: number) => {
  d3.selectAll(`.playerNameText${playerId}`).raise();
  d3.selectAll(`.playerName${playerId}`).raise();
}

const lowerPlayerName = () => {
  d3.selectAll('.scoreCircle').raise();
  d3.selectAll('.scoreText').raise();
}

export const drawPlayerImages = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  setSwapHoverPlayer: React.Dispatch<React.SetStateAction<number>>,
  player: ChemistryPlayerPositions,
  positionResults: ChemistryPlayerPositions[],
  theme: Theme,
  isIndividualVis: boolean,
  alreadyPlottedPlayer?: AlreadyPlottedPlayer,
  handleReset?: () => void,
  setIsIndividualChemistry?: (value: React.SetStateAction<boolean>) => void,
  handleIndividualChemistry?: (playerID: number, positionCode: string) => void,
  handleSwapPlayer?: (playerToSwapWith: number, playerToGoOut?: number, swapPosition?: string, secondSwapPlayer?: ChemistryPlayerPositions) => Promise<void>
) => {
  const circleSize = window.innerWidth < 800 || window.innerHeight < 800 ? 18 : 25;
  const imageSize = window.innerWidth < 800 || window.innerHeight < 800 ? 34 : 50;
  const centerX = xScale(player.xCoordinate);
  const centerY = yScale(player.yCoordinate);
  svg.append("clipPath").attr("id", "playerImageClipPath");
  function circlesIntersect(x1: number, y1: number, r1: number, x2: number, y2: number, r2: number) {
    const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    return distance <= r1 + r2;
  }
  function dragstarted(event: any) {
    // @ts-ignore
    d3.select(this).raise().classed("active", true);
  }
  function dragged(event: any) {
    // @ts-ignore
    d3.select(this)
      .attr("x", event.x - imageSize / 2)
      .attr("y", event.y - imageSize / 2);
    svg.selectAll(`.circle-image${player.playerId}`)
      .attr("cx", event.x)
      .attr("cy", event.y);
  }
  function dragended(event: any, d: any) {
    // @ts-ignore
    d3.select(this).classed("active", false);
    const imageX = event.x - imageSize / 2;
    const imageY = event.y - imageSize / 2;
    const imageRadius = imageSize / 2;
    let canDrop = false;
    let targetX = 0;
    let targetY = 0;
    let targetPlayerId = 0;
    let targetPositionCode = "";
    svg.selectAll(".circle-image")
      .filter(function() {
        return !d3.select(this).classed(`circle-image${player.playerId}`);
      })
      .each(function () {
        const circle = d3.select(this);
        const circleX = +circle.attr("cx");
        const circleY = +circle.attr("cy");
        const circleRadius = +circle.attr("r");
        const circleClass = circle.attr("class");
        const playerIdMatch = circleClass.match(/circle-image(\d+)/);
        const circlePlayerId = playerIdMatch ? parseInt(playerIdMatch[1]) : 0;
        if (circlesIntersect(imageX, imageY, imageRadius, circleX, circleY, circleRadius)) {
          canDrop = true;
          targetX = circleX;
          targetY = circleY;
          const playerFound = positionResults?.find((player) => player.playerId === circlePlayerId);
          if (playerFound) {
            targetPositionCode = playerFound?.positionCode;
          }
          targetPlayerId = circlePlayerId;
          return false; // Break the loop if a collision is found
        }
      });
  
    if (canDrop) {
      // @ts-ignore
      d3.select(this)
        .transition()
        .duration(200)
        .attr("x", targetX - imageSize/2)
        .attr("y", targetY - imageSize/2);  
      svg.selectAll(`.circle-image${player.playerId}`)
        .transition()
        .duration(200)
        .attr("cx", targetX)
        .attr("cy", targetY);
      handleSwapPlayer!(player.playerId, targetPlayerId, targetPositionCode, player);
    } else {
      // @ts-ignore
      d3.select(this).transition().duration(200).attr("x", d.originalX).attr("y", d.originalY);
      svg.selectAll(`.circle-image${player.playerId}`)
        .transition()
        .duration(200)
        .attr("cx", d.originalX + imageSize/2)
        .attr("cy", d.originalY + imageSize/2);
    }
  }  
  if (player.playerId !== alreadyPlottedPlayer?.playerID) {
    svg
      // @ts-ignore
      .append("circle")
      .attr("class", `circle-image circle-image${player.playerId}`)
      .attr("r", circleSize)
      .style("fill",  player.playerId > 0 ? "#D9D9D9" : "none")
      .attr("stroke-width", 1)
      .attr("stroke", theme.playerCardBorderColor)
      .attr("stroke-dasharray", player.playerId > 0 ? "none" : "5,5")
      .attr("cx", centerX)
      .attr("cy", centerY)
      .datum({
        originalX: centerX - imageSize / 2,
        originalY: centerY - imageSize / 2,
      });

    const imageElement = svg
      .append("image")
      .attr("xlink:href", player.playerImageURL ? player.playerImageURL : noImagePlayer)
      .attr("width", imageSize)
      .attr("height", imageSize)
      .attr("class", "circle-image")
      .attr("cursor", "pointer")
      .attr("x", centerX - imageSize / 2)
      .attr("y", centerY - imageSize / 2)
      .attr('opacity', !player.playerId || player.playerId <= 0 ?  0 : 1)
       .on("dragover", (event: any) => {
        // debugger
          event.preventDefault();
        })
        .on("drop", (event: any) => {
          // debugger
          event.preventDefault();
          const playerId = event.dataTransfer.getData("playerId");

          // setSidebarPlayerShow(false);
          // setIsReplacePlayer(true);
          setSwapHoverPlayer(Number(playerId));
          // setSwapPositionCode(player.positionCode);
          // setPlayerCardID(Number(playerId));
          if (handleSwapPlayer) {
            handleSwapPlayer(Number(playerId), player.playerId, player.positionCode);
          }
        })
      .datum({
        originalX: centerX - imageSize / 2,
        originalY: centerY - imageSize / 2,
      });
    // Only apply drag behavior if not in individual visualization mode
    if (!isIndividualVis) {
      imageElement
        // @ts-ignore
        .call(d3.drag().on("start", dragstarted).on("drag", dragged).on("end", dragended));
      svg.select(`.circle-image${player.playerId}`)
        // @ts-ignore
        .call(d3.drag().on("start", dragstarted).on("drag", dragged).on("end", dragended));
    }
    imageElement
      .on("mouseover", () => {
        showHoverIcons(svg, player);
        setSwapHoverPlayer(Number(player.playerId));
        raisePlayerNames(player.playerId);
      })
      .on("mouseout", () => {
        hideHoverIcons(svg, player);
        setSwapHoverPlayer(0);
        lowerPlayerName();
      })
      .on("click", () => {
        if (isIndividualVis) {
          handleReset!();
          setIsIndividualChemistry!(false);
        } else {
          handleIndividualChemistry!(player.playerId, player.positionCode);
        }
      });
  }
};
export const drawPlayerNames = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  players: ChemistryPlayerPositions[],
  theme: Theme,
  isPerformancSwitchToggled: boolean,
  individuallySelectedPlayer?: number | null,
  oldPlayerId?: number,
  newPlayerId?: number
) => {
  players.map((player) => {
    const centerX = xScale(player.xCoordinate);
    const centerY = yScale(player.yCoordinate);
    const textPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + 54 / 2 + 8 : centerY + 30 + 13;
    const nametext = svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", "none")
      .text(player?.playerId === 365443 ? "DE LIGT" : player?.shortName?.split(/\.|\s/)?.filter(Boolean).slice(1).join(" ")?.toUpperCase() || player?.shortName?.toUpperCase())
      .style("font", "10px Aeonik");
    const textWidth = nametext.node()!.getBBox();
    const isSelectedPlayer = player.playerId === individuallySelectedPlayer;
    player.playerId > 0 &&
      appendRect(
        svg,
        textWidth.width + 2 * 6,
        textWidth.height + 2 * 6,
        centerX - (textWidth.width + 2 * 6) / 2,
        textPosition,
        isSelectedPlayer ? "#00A1E4" : player.playerId === newPlayerId ? "#00D1A8" : player.playerId === oldPlayerId ? "#00A1E4" : theme.secondaryButtonBackground,
        0,
        1,
        isSelectedPlayer ? "#0072A2" : player.playerId === newPlayerId ? "#00BE99" : player.playerId === oldPlayerId ? "#0072A2" : theme.secondaryButtonBorderColor,
        1,
        `playerNameText playerNameText${player.playerId}`,
        `translate(-${0}, -${textWidth.height * 0.9 + 6})`
      );
    appendText(
      svg,
      centerX,
      textPosition,
      player?.playerId === 365443 ? "DE LIGT" : player?.shortName?.split(/\.|\s/)?.filter(Boolean).slice(1).join(" ")?.toUpperCase() || player?.shortName?.toUpperCase(),
      isSelectedPlayer ? "white" : player.playerId === oldPlayerId ? "white" : theme.playerColor,
      "Aeonik",
      600,
      10,
      1,
      "middle",
      `playerName playerName${player.playerId}`
    );
    const contributionValueRectPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + 42 : centerY + 50;
    const contributionValueTextPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + 52 : centerY + 61;
    isPerformancSwitchToggled &&
      player.playerId > 0 && player?.positionCode !== 'gk' &&
      appendRect(
        svg,
        player.contributionValue?.toString().length > 2 ? 28 : 20,
        15,
        centerX - (player.contributionValue?.toString().length > 2 ? 15 : 8),
        contributionValueRectPosition,
        getColorByValue(player?.contributionValue),
        0,
        0,
        "none",
        1
      );
    isPerformancSwitchToggled &&
      player.playerId > 0 && player?.positionCode !== 'gk' &&
      appendText(svg, centerX + 8, contributionValueTextPosition, player.contributionValue, player?.contributionValue < 39 ? "#FFFFFF" : "#111111", "DM Mono", 500, 10, 1, "end");
  });
};
export const drawPlayerNamesForHulls = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  players: HullData,
  theme: Theme,
  isPerformancSwitchToggled: boolean,
  setIndividualHullPlayer: React.Dispatch<React.SetStateAction<number>>,
  individualHullPlayer: number
) => {
  players.map((player) => {
    const centerX = xScale(player.avgX);
    const centerY = yScale(player.avgY);
    const textPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + 54 / 2 + 8 : centerY + 30 + 13;
    const nametext = svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", theme.playerColor)
      .text(player.playerName?.toUpperCase())
      .style("font-family", "10px Aeonik")
      .style("font-weight", 600)
      .style("font-size", "10px");
    const textWidth = nametext.node()!.getBBox();
    {
      player.playerId > 0 &&
        appendRect(
          svg,
          textWidth.width + 2 * 6,
          textWidth.height + 2 * 6,
          centerX - (textWidth.width + 2 * 6) / 2,
          textPosition,
          theme.secondaryButtonBackground,
          0,
          1,
          theme.secondaryButtonBorderColor,
          1,
          "none",
          `translate(-${0}, -${textWidth.height * 0.9 + 6})`
        );
    }
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", theme.playerColor)
      .text(player.playerName?.toUpperCase())
      .style("font-family", "Aeonik")
      .style("font-weight", 600)
      .style("font-size", "10px")
      .style("cursor", "pointer")
      .on("click", () => {
        if (individualHullPlayer === player.playerId) {
          setIndividualHullPlayer(0);
        } else {
          setIndividualHullPlayer(player.playerId);
        }
      });
    {
      isPerformancSwitchToggled && player.playerId > 0 && appendRect(svg, 30, 15, centerX - 15, centerY + 50, "#4AF075", 0, 0, "none", 1);
    }
    {
      isPerformancSwitchToggled &&
        player.playerId > 0 &&
        appendText(svg, centerX + 8, centerY + 61, "100", "black", "DM Mono", 500, 10, 1, "end");
    }
  });
};
export const drawPlayerHoverIcons = (
  currentView: CurrentView,
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  player: ChemistryPlayerPositions,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  theme: Theme,
  setSwapHoverPlayer: (value: React.SetStateAction<number>) => void,
  setSidebarPlayerShow: (value: React.SetStateAction<boolean>) => void,
  setSwapPositionCode: (value: React.SetStateAction<string>) => void,
  setPlayerCardID: (value: React.SetStateAction<number>) => void,
  isForSimulation?: boolean
) => {
  const centerX = xScale(player.xCoordinate);
  const centerY = yScale(player.yCoordinate);
  if (currentView === "systemFit") {
    if (!isForSimulation) {
        svg
      .append("rect")
      .attr("class", `hoverRect${player.playerId}`)
      .style("opacity", 0)
      .attr("x", centerX + 28 / 2)
      .attr("y", centerY - 44)
      .style("fill", theme.secondaryButtonBackground)
      .attr("rx", 2)
      .attr("ry", 2)
      .attr("width", 32)
      .attr("height", 35)
      .attr("stroke-width", 1)
      .attr("stroke", theme.secondaryButtonBorderColor)
      .on("mouseover", function () {
        showHoverText(svg, player);
        showHoverIcons(svg, player);
        setSwapHoverPlayer(player.playerId);
        raisePlayerNames(player.playerId);
      })
      .on("mouseout", () => {
        hideHoverText(svg, player);
        hideHoverIcons(svg, player);
        setSwapHoverPlayer(0);
        lowerPlayerName();
      })
      .on("click", () => {
        setSidebarPlayerShow(true);
        setSwapPositionCode(player.positionCode);
      });
    }
  }
  svg
    .append("rect")
    .attr("class", `hoverRect${player.playerId}`)
    .style("opacity", 0)
    .attr("x", centerX + 28 / 2)
    .attr("y", centerY - (currentView === "systemFit" ? 10 : 40))
    .style("fill", theme.secondaryButtonBackground)
    .attr("rx", 2)
    .attr("ry", 2)
    .attr("width", 32)
    .attr("height", 35)
    .attr("stroke-width", 1)
    .attr("stroke", theme.secondaryButtonBorderColor)
    .on("mouseover", () => {
      showHoverText(svg, player);
      showHoverIcons(svg, player);
      setSwapHoverPlayer(player.playerId);
      raisePlayerNames(player.playerId);
    })
    .on("mouseout", () => {
      hideHoverText(svg, player);
      hideHoverIcons(svg, player);
      setSwapHoverPlayer(0);
      lowerPlayerName();
    })
    .on("click", () => {
      setPlayerCardID(player.playerId);
    });

  if (currentView === "systemFit") {
      if(!isForSimulation){
        svg
              .append("svg:image")
              .attr("class", `hoverIcon${player.playerId}`)
              .style("opacity", 0)
              .attr("xlink:href", () => SwapPlayer)
              .attr("x", centerX + 22)
              .attr("y", centerY - 70 / 2)
              .attr("width", 18)
              .attr("height", 18)
              .style("filter", theme.PitchColor === "#1B1B1B" ? "invert(100%) hue-rotate(180deg) brightness(100%)" : "none")
              .on("mouseover", () => {
                showHoverText(svg, player);
                showHoverIcons(svg, player);
                setSwapHoverPlayer(player.playerId);
                raisePlayerNames(player.playerId);
              })
              .on("mouseout", () => {
                hideHoverText(svg, player);
                hideHoverIcons(svg, player);
                setSwapHoverPlayer(0);
                lowerPlayerName();
              })
              .on("click", () => {
                setSidebarPlayerShow(true);
                setSwapPositionCode(player.positionCode);
              });
      }
  }
  svg
    .append("svg:image")
    .attr("class", `hoverIcon${player.playerId}`)
    .style("opacity", 0)
    .attr("xlink:href", () => VisibleON)
    .attr("x", centerX + 22)
    .attr("y", centerY - (currentView === "systemFit" ? 4 : 63) / 2)
    .attr("width", 18)
    .attr("height", 18)
    .style("filter", theme.PitchColor === "#1B1B1B" ? "invert(100%) hue-rotate(180deg) brightness(100%)" : "none")
    .on("mouseover", () => {
      showHoverText(svg, player);
      showHoverIcons(svg, player);
      setSwapHoverPlayer(player.playerId);
      raisePlayerNames(player.playerId);
    })
    .on("mouseout", () => {
      hideHoverText(svg, player);
      hideHoverIcons(svg, player);
      setSwapHoverPlayer(0);
      lowerPlayerName();
    })
    .on("click", () => {
      setPlayerCardID(player.playerId);
    });
  if (currentView === "systemFit") {
      if(!isForSimulation){
        svg
              .append("text")
              .attr("class", `hoverText${player.playerId}`)
              .attr("text-anchor", "end")
              .attr("dominant-baseline", "bottom")
              .attr("x", centerX + 216 / 2)
              .attr("y", centerY - 20)
              .text("Swap out")
              .style("fill", theme.playerColor)
              .style("opacity", 0)
              .on("mouseover", function () {
                showHoverText(svg, player);
                showHoverIcons(svg, player);
                setSwapHoverPlayer(player.playerId);
                raisePlayerNames(player.playerId);
              })
              .on("mouseout", function () {
                hideHoverText(svg, player);
                hideHoverIcons(svg, player);
                setSwapHoverPlayer(0);
                lowerPlayerName();
              })
              .on("click", () => {
                setSidebarPlayerShow(true);
                setSwapPositionCode(player.positionCode);
              })
              .attr("cursor", "pointer")
              .style("font", "15px Aeonik")
              .style("font-weight", "500");
      }
  }
  svg
    .append("text")
    .attr("class", `hoverText${player.playerId}`)
    .attr("text-anchor", "end")
    .attr("dominant-baseline", "bottom")
    .attr("x", centerX + 229 / 2)
    .attr("y", centerY - (currentView === "systemFit" ? -12 : 17))
    .text("Player info")
    .style("fill", theme.playerColor)
    .style("opacity", 0)
    .on("mouseover", function () {
      showHoverText(svg, player);
      showHoverIcons(svg, player);
      setSwapHoverPlayer(player.playerId);
      raisePlayerNames(player.playerId);
    })
    .on("mouseout", function () {
      hideHoverText(svg, player);
      hideHoverIcons(svg, player);
      setSwapHoverPlayer(0);
      lowerPlayerName();
    })
    .on("click", () => {
      setPlayerCardID(player.playerId);
    })
    .attr("cursor", "pointer")
    .style("font", "15px Aeonik")
    .style("font-weight", "500");
};



export const drawEventsNamesForHulls = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  players: IGamemodelData[],
  theme: Theme,
  setIndividualHullPlayer: React.Dispatch<React.SetStateAction<string>>,
  individualHullPlayer: string
) => {
  players.map((player) => {
    const centerX = xScale(player.avgX);
    const centerY = yScale(player.avgY);
    const textPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + 54 / 2 + 8 : centerY + 30 + 13;
    const nametext = svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", theme.playerColor)
      .text(player.name?.toUpperCase())
      .style("font-family", "10px Aeonik")
      .style("font-weight", 600)
      .style("font-size", "10px");
    const textWidth = nametext.node()!.getBBox();
    {
      player?.playerId.length > 0 &&
        appendRect(
          svg,
          textWidth.width + 2 * 6,
          textWidth.height + 2 * 6,
          centerX - (textWidth.width + 2 * 6) / 2,
          textPosition,
          theme.secondaryButtonBackground,
          0,
          1,
          theme.secondaryButtonBorderColor,
          1,
          "none",
          `translate(-${0}, -${textWidth.height * 0.9 + 6})`
        );
    }
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", theme.playerColor)
      .text(player.name?.toUpperCase())
      .style("font-family", "Aeonik")
      .style("font-weight", 600)
      .style("font-size", "10px")
      // .style("cursor", "pointer")
      // .on("click", () => {
      //   if (individualHullPlayer === player.name) {
      //     setIndividualHullPlayer('');
      //   } else {
      //     setIndividualHullPlayer(player.name);
      //   }
      // });
      
    // {
    //   player?.playerId.length > 0 && appendRect(svg, 30, 15, centerX - 15, centerY + 50, "#4AF075", 0, 0, "none", 1);
    // }
    // {
    //   player?.playerId.length > 0 &&
    //     appendText(svg, centerX + 8, centerY + 61, "100", "black", "DM Mono", 500, 10, 1, "end");
    // }
  });
};



export const drawGameModelPlayers = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  players: any[], // IGameModelPlayerDetail[]
  theme: Theme,
  setSidebarPlayerShow: React.Dispatch<React.SetStateAction<boolean>>,
  setPlayerCardID: React.Dispatch<React.SetStateAction<number>>,
  isVertical?: boolean
) => {
  const circleSize = window.innerWidth < 800 || window.innerHeight < 800 ? 18 : 25;
  const imageSize = window.innerWidth < 800 || window.innerHeight < 800 ? 34 : 50;

  players.map((player) => {
    const centerX = xScale(player.avgX);
    const centerY = yScale(player.avgY);

    const playerGroup = svg.append("g");
    if (isVertical) {
      playerGroup.attr("transform", `rotate(90 ${centerX} ${centerY})`);
    }

    // Draw player circle
    playerGroup
      // @ts-ignore
      .append("circle")
      .attr("class", `gameModelPlayerCircle-${player.playerId}`)
      .attr("r", circleSize)
      .style("fill", player.playerId > 0 ? "#D9D9D9" : "none")
      .attr("stroke-width", 1)
      .attr("stroke", theme.playerCardBorderColor)
      .attr("stroke-dasharray", player.playerId > 0 ? "none" : "5,5")
      .attr("cx", centerX)
      .attr("cy", centerY);

    // Draw player image
    const imageElement = playerGroup
      .append("image")
      .attr("xlink:href", player.playerImageURL ? player.playerImageURL : noImagePlayer)
      .attr("width", imageSize)
      .attr("height", imageSize)
      .attr("class", `gameModelPlayerImage-${player.playerId}`)
      .attr("cursor", "pointer")
      .attr("x", centerX - imageSize / 2)
      .attr("y", centerY - imageSize / 2)
      .attr("opacity", !player.playerId || player.playerId <= 0 ? 0 : 1)
      .on("click", () => {
        setPlayerCardID(player.playerId);
        setSidebarPlayerShow(true);
      })

    // Draw player name
    const textPosition = window.innerWidth < 800 || window.innerHeight < 800 ? centerY + imageSize / 2 + 8 : centerY + imageSize / 2 + 13;
    const shortName = player.shortName?.toUpperCase() || "";

    const nametext = playerGroup
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "bottom")
      .attr("x", centerX)
      .attr("y", textPosition)
      .attr("fill", "none")
      .text(shortName)
      .style("font", "10px Aeonik");

    const textWidth = nametext.node()!.getBBox();

    // Background for player name
    if (player.playerId > 0) {
      appendRect(
        playerGroup,
        textWidth.width + 2 * 6,
        textWidth.height + 2 * 6,
        centerX - (textWidth.width + 2 * 6) / 2,
        textPosition,
        theme.secondaryButtonBackground,
        0,
        1,
        theme.secondaryButtonBorderColor,
        1,
        `gameModelPlayerNameBg-${player.playerId}`,
        `translate(-${0}, -${textWidth.height * 0.9 + 6})`
      );

      appendText(
        playerGroup,
        centerX,
        textPosition,
        shortName,
        theme.playerColor,
        "Aeonik",
        600,
        10,
        1,
        "middle",
        `gameModelPlayerName-${player.playerId}`
      );
    }
  });
};

export const drawGameModelHull = (
  svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
  pitchGroup: d3.Selection<SVGGElement, any, null, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  hullData: IGamemodelData[],
  theme?: Theme,
  setIndividualHullPlayer? : React.Dispatch<React.SetStateAction<string>>,
  individualHullPlayer? : string,

) => {
  
  if (hullData.length > 1) {
     const archetypeColorIndexes = [
      "#00D1A8",
      "#4AF075",
      "#C9F0B5",
      "#FFC2C2",
      "#FF7800",
      "#64ADDB",
      "#FF3864",
      "#00A1E4",
      "#F2E94E",
    ];
    
    drawEventsNamesForHulls(svg, xScale, yScale, hullData, theme!, setIndividualHullPlayer!, individualHullPlayer!);
    const playerNames = hullData?.map(player => player?.name!) ?? [];
    const colorScale = d3.scaleOrdinal<string, string>()
  .domain(playerNames)
  .range(d3.schemeCategory10); // or d3.schemeSet3, etc.
    hullData?.map(player => {
 

      // {player.latLongs.map(d => {
    //     pitchGroup
    //       .append("circle")
    //       .attr("cx", xScale(player.avgX))
    //       .attr("cy", yScale(player.avgY))
    //       .attr("r", 3)
    //       .style("fill", theme!.playerColor);
    //   // }
    // )}
     // Step 1: Get all unique player names (somewhere outside the rendering loop)
// / assuming `players` is your full data


// Inside your loop:
if (player.latLongs.length >= 3) {
  const hullPoints = d3.polygonHull(
    player.latLongs.map((d) => [
      xScale(d.locationX),
      yScale(d.locationY),
    ])
  );

  const lineGenerator = d3
    .line()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(d3.curveBasisClosed);
    // .curve(d3.curveLinearClosed)

  pitchGroup
    .append("path")
    .datum(hullPoints)
    // @ts-ignore
    .attr("d", lineGenerator)
    .attr("fill", colorScale(player.name))
    .style("fill-opacity", individualHullPlayer
      ? player.name === individualHullPlayer ? 0.75 : 0.1
      : 0.5)
    
    .style("stroke", theme!.playerDataBorder)
    
    .style("stroke-width", individualHullPlayer && player.name !== individualHullPlayer
      ? 2
      : 4);
    


    pitchGroup
    .append("circle")
    .attr("cx", xScale(player.avgX))
    .attr("cy", yScale(player.avgY))
    .attr("r", 6)
    .style("fill", 'white');
}

    })
  }
}
